import { useEffect, useRef, useState } from "react";
import { Box, Center, Loader, Text } from "@mantine/core";
import * as pdfjsLib from "pdfjs-dist";
import {
  ReportAttachmentPage,
  type ReportFooterProps,
  type ReportHeaderSectionProps,
} from "./ReportAttachmentPage";

pdfjsLib.GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL}/pdf.worker.min.js`;

interface PdfPagesRendererProps {
  url: string;
  label?: string;
  onReady?: (pageCount: number) => void;
  onError?: () => void;
  reportPageFrame?: {
    headerSectionProps?: ReportHeaderSectionProps;
    footerProps?: Omit<ReportFooterProps, "pageNumber" | "totalPages">;
    firstPageNumber: number;
    totalPages: number;
  };
}

export function PdfPagesRenderer({
  url,
  label,
  onReady,
  onError,
  reportPageFrame,
}: PdfPagesRendererProps) {
  const [pageDataUrls, setPageDataUrls] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const renderingRef = useRef(false);

  useEffect(() => {
    if (!url || renderingRef.current) return;
    renderingRef.current = true;
    setLoading(true);
    setError(null);

    let cancelled = false;

    (async () => {
      try {
        const pdf = await pdfjsLib.getDocument(url).promise;
        const pageNumbers = Array.from(
          { length: pdf.numPages },
          (_, i) => i + 1
        );
        const pages = await pageNumbers.reduce(async (accPromise, pageNum) => {
          const acc = await accPromise;
          if (cancelled) return acc;
          const page = await pdf.getPage(pageNum);
          const scale = 2;
          const viewport = page.getViewport({ scale });

          const canvas = document.createElement("canvas");
          canvas.width = viewport.width;
          canvas.height = viewport.height;
          const ctx = canvas.getContext("2d")!;

          await (page.render({ canvasContext: ctx, viewport } as any) as any)
            .promise;
          acc.push(canvas.toDataURL("image/png"));
          page.cleanup();
          return acc;
        }, Promise.resolve([] as string[]));

        if (!cancelled) {
          setPageDataUrls(pages);
          setLoading(false);
          onReady?.(pages.length);
        }
      } catch {
        if (!cancelled) {
          setError("This PDF attachment could not be rendered.");
          setLoading(false);
          onError?.();
        }
      } finally {
        renderingRef.current = false;
      }
    })();

    return () => {
      cancelled = true;
      renderingRef.current = false;
    };
  }, [onError, onReady, url]);

  if (loading) {
    return (
      <Center py="xl">
        <Loader size="sm" />
      </Center>
    );
  }

  if (error) {
    return (
      <Text size="sm" c="red" ta="center" py="md">
        {error}
      </Text>
    );
  }

  return (
    <>
      {pageDataUrls.map((dataUrl, index) => {
        const content = (
          <Box
            style={{
              width: "100%",
              height: "100%",
              minHeight: 0,
              overflow: "hidden",
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
            }}
          >
            {index === 0 && label && (
              <Text size="xs" c="dimmed" mb={4}>
                {label}
              </Text>
            )}
            <img
              src={dataUrl}
              alt={`${label || "PDF"} — Page ${index + 1}`}
              style={{
                width: "100%",
                minHeight: 0,
                flex: 1,
                maxHeight: "100%",
                objectFit: "contain",
                display: "block",
              }}
            />
          </Box>
        );

        if (!reportPageFrame) {
          return (
            <Box
              key={index}
              mb="md"
              style={{ pageBreakBefore: index > 0 ? "always" : undefined }}
            >
              {content}
            </Box>
          );
        }

        const pageNumber = reportPageFrame.firstPageNumber + index;
        return (
          <ReportAttachmentPage
            key={index}
            headerSectionProps={reportPageFrame.headerSectionProps}
            footerProps={
              reportPageFrame.footerProps
                ? {
                    ...reportPageFrame.footerProps,
                    pageNumber,
                    totalPages: reportPageFrame.totalPages,
                  }
                : undefined
            }
          >
            {content}
          </ReportAttachmentPage>
        );
      })}
    </>
  );
}
